/**
 * Tier 3 Item 37 — Native push registration for the Capacitor wrapper.
 *
 * Lives in the SPA (not the mobile/ shim) because the WebView immediately
 * navigates server.url -> silvergames.ca, so anything we need to run on
 * native must be bundled with the live web build.
 *
 * No-op on plain web: `Capacitor.isNativePlatform()` is false unless the
 * runtime injected by the iOS/Android shell is present. Capacitor's plugins
 * are loaded dynamically so the web bundle doesn't drag in native code.
 */
const ENDPOINT = '/api/push/register';

function jwt() {
  try { return localStorage.getItem('token') || localStorage.getItem('admin_token') || null; }
  catch { return null; }
}

async function postToken(token, platform) {
  const headers = { 'Content-Type': 'application/json' };
  const t = jwt();
  if (t) headers.Authorization = `Bearer ${t}`;
  try {
    const r = await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token, platform, kind: 'native' }),
    });
    if (!r.ok) console.warn('Native push register failed:', r.status);
  } catch (e) { console.warn('Native push register error:', e.message); }
}

export async function initNativePush() {
  // Detect Capacitor without importing it on web (would bloat the bundle).
  const cap = (typeof window !== 'undefined') ? window.Capacitor : null;
  if (!cap || !cap.isNativePlatform || !cap.isNativePlatform()) return;

  try {
    const [{ PushNotifications }, { App }] = await Promise.all([
      import(/* webpackChunkName: "cap-push" */ '@capacitor/push-notifications'),
      import(/* webpackChunkName: "cap-app" */ '@capacitor/app'),
    ]).catch(e => { console.warn('Capacitor plugins not bundled:', e.message); return []; });

    if (!PushNotifications) return;

    const platform = cap.getPlatform ? cap.getPlatform() : 'unknown';

    const perm = await PushNotifications.requestPermissions().catch(() => ({ receive: 'denied' }));
    if (perm.receive !== 'granted') return;

    await PushNotifications.register();

    PushNotifications.addListener('registration', (t) => postToken(t.value, platform));
    PushNotifications.addListener('registrationError', (err) => console.error('Push registration error', err));
    PushNotifications.addListener('pushNotificationReceived', (n) => {
      if (typeof window.__sgInAppNotify === 'function') {
        window.__sgInAppNotify({ title: n.title, body: n.body, data: n.data });
      }
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const target = action?.notification?.data?.url;
      if (target) window.location.href = target;
    });

    if (App?.addListener) {
      App.addListener('appUrlOpen', (data) => {
        if (!data?.url) return;
        try {
          const u = new URL(data.url);
          window.location.href = `${u.pathname}${u.search}`;
        } catch (_) {}
      });
    }
  } catch (e) {
    console.warn('initNativePush failed:', e.message);
  }
}
